/**
 * Scroll-ahead image prefetch + pic-lazy fade-in.
 * Walks .shopify-section in DOM order so any section type works as hero.
 */
(function () {
  const PREFETCH_ROOT_MARGIN = "100% 0px 100% 0px";
  const IDLE_PREFETCH_SECTIONS = 3;
  const INDEX_IDLE_PREFETCH_SECTIONS = 8;
  const INDEX_EARLY_PRELOAD_SECTIONS = 6;

  const isIndex = () => document.body.classList.contains("template-index");
  const isProduct = () => document.body.classList.contains("template-product");

  const prefetchedUrls = new Set();

  function markPicLazyLoaded(img) {
    const wrap = img.closest(".pic-lazy");
    if (!wrap || wrap.classList.contains("loaded")) return;
    if (img.complete && img.naturalWidth > 0) {
      wrap.classList.add("loaded");
    }
  }

  function prefetchImage(url, img, usePreload) {
    if (!url || prefetchedUrls.has(url)) return;
    prefetchedUrls.add(url);

    const link = document.createElement("link");
    link.rel = usePreload ? "preload" : "prefetch";
    link.as = "image";
    link.href = url;
    document.head.appendChild(link);

    const loader = new Image();
    loader.decoding = "async";
    loader.onload = () => {
      if (img) markPicLazyLoaded(img);
      document.querySelectorAll(".pic-lazy img").forEach((candidate) => {
        const candidateUrl = resolvePrefetchUrl(candidate);
        if (candidateUrl === url) markPicLazyLoaded(candidate);
      });
    };
    loader.src = url;
  }

  function resolvePrefetchUrl(img) {
    if (img.currentSrc) return img.currentSrc;
    if (img.src) return img.src;
    return null;
  }

  function prefetchLazyImages(root, options = {}) {
    const usePreload = options.usePreload === true;
    root.querySelectorAll('img[loading="lazy"]:not([data-am-prefetched])').forEach((img) => {
      const url = resolvePrefetchUrl(img);
      if (!url) return;
      img.dataset.amPrefetched = "true";
      prefetchImage(url, img, usePreload);
    });
  }

  function initPicLazy() {
    document.querySelectorAll(".pic-lazy img").forEach((img) => {
      const wrap = img.closest(".pic-lazy");
      if (!wrap || wrap.classList.contains("loaded")) return;

      const markLoaded = () => wrap.classList.add("loaded");

      if (img.complete && img.naturalWidth > 0) {
        markLoaded();
        return;
      }

      img.addEventListener("load", markLoaded, { once: true });
      if (img.decode) {
        img.decode().then(markLoaded).catch(markLoaded);
      }
    });
  }

  function initSectionPrefetch() {
    const sections = Array.from(document.querySelectorAll(".shopify-section"));
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          prefetchLazyImages(entry.target, { usePreload: isIndex() });
        });
      },
      { rootMargin: PREFETCH_ROOT_MARGIN },
    );

    sections.forEach((section) => observer.observe(section));

    const idleLimit = isIndex() ? INDEX_IDLE_PREFETCH_SECTIONS : IDLE_PREFETCH_SECTIONS;

    const idlePrefetch = (startIndex) => {
      let count = 0;
      for (let i = startIndex; i < sections.length && count < idleLimit; i++) {
        prefetchLazyImages(sections[i], { usePreload: isIndex() && i < INDEX_EARLY_PRELOAD_SECTIONS });
        count++;
      }
    };

    const runIdleQueue = () => {
      const firstLazySection = sections.findIndex((section) =>
        section.querySelector('img[loading="lazy"]'),
      );
      if (firstLazySection === -1) return;
      idlePrefetch(firstLazySection);
    };

    if (isIndex()) {
      const earlyCount = Math.min(INDEX_EARLY_PRELOAD_SECTIONS, sections.length);
      for (let i = 0; i < earlyCount; i++) {
        prefetchLazyImages(sections[i], { usePreload: true });
      }
    }

    if ("requestIdleCallback" in window) {
      requestIdleCallback(runIdleQueue, { timeout: isIndex() ? 1500 : 3000 });
    } else {
      setTimeout(runIdleQueue, isIndex() ? 800 : 1500);
    }
  }

  function tuneHeaderLogoPriority() {
    const isMobile = window.matchMedia("(max-width: 599px)").matches;
    const logo =
      document.querySelector(".wt-header__logo__img--responsive:not(.wt-header__logo__img--transparent)") ||
      (isMobile
        ? document.querySelector(".wt-header__logo__img--mobile:not(.wt-header__logo__img--transparent)")
        : document.querySelector(
            ".wt-header__logo__img:not(.wt-header__logo__img--mobile):not(.wt-header__logo__img--transparent)",
          )) ||
      document.querySelector(".wt-header__logo__img--transparent.wt-header__logo__img--about-us-uploaded");

    if (!logo) return;

    if (isProduct()) {
      logo.loading = "lazy";
      logo.setAttribute("fetchpriority", "low");
      return;
    }

    logo.loading = "eager";
    logo.setAttribute("fetchpriority", isIndex() ? "auto" : "high");
  }

  function prefetchProductGalleryRest(gallery) {
    prefetchLazyImages(gallery, { usePreload: false });
  }

  function initProductGalleryPriority() {
    if (!isProduct()) return;

    const gallery =
      document.querySelector("gallery-section.wt-product__gallery") ||
      document.querySelector(".wt-product__gallery");
    if (!gallery) return;

    const featured =
      gallery.querySelector('img[fetchpriority="high"]') ||
      gallery.querySelector('img[loading="eager"]') ||
      gallery.querySelector("img");

    const scheduleGalleryPrefetch = () => prefetchProductGalleryRest(gallery);

    if (featured && !(featured.complete && featured.naturalWidth > 0)) {
      featured.addEventListener("load", scheduleGalleryPrefetch, { once: true });
    } else {
      scheduleGalleryPrefetch();
    }
  }

  function initHeroImageReady() {
    document.querySelectorAll(".am-collection-list--hero").forEach((hero) => {
      if (hero.classList.contains("am-hero-images-ready")) return;

      const isDesktop = window.matchMedia("(min-width: 900px)").matches;
      const images = Array.from(hero.querySelectorAll(".collection-list__item"))
        .filter((item) => isDesktop || !item.classList.contains("am-collection-list__item--hide-mobile"))
        .map((item) => item.querySelector(".card__img:not(.card__img--hover)"))
        .filter(Boolean);

      // Mobile: only the primary slide gates the reveal (peek slide must not blank the hero).
      const gateImages = isDesktop ? images : images.slice(0, 1);

      let revealed = false;
      const reveal = () => {
        if (revealed) return;
        revealed = true;
        hero.classList.add("am-hero-images-ready");
        hero.classList.remove("am-hero-images-pending");
      };

      if (!gateImages.length) {
        reveal();
        return;
      }

      if (gateImages.every((img) => img.complete && img.naturalWidth > 0)) {
        reveal();
        return;
      }

      hero.classList.add("am-hero-images-pending");

      let pending = gateImages.length;
      const checkAllReady = () => {
        pending -= 1;
        if (pending <= 0) reveal();
      };

      gateImages.forEach((img) => {
        img.loading = "eager";
        img.setAttribute("fetchpriority", "high");

        const finish = () => {
          // Reveal on load — do not wait on decode() (can hang on Android).
          checkAllReady();
        };

        img.addEventListener('error', checkAllReady, { once: true });

        if (img.complete && img.naturalWidth > 0) finish();
        else img.addEventListener("load", finish, { once: true });
      });

      // Safety net: never leave the hero blank on slow/flaky mobile loads.
      setTimeout(reveal, 800);
    });
  }

  function tuneHomepageHeroImagePriority() {
    if (!isIndex()) return;

    const hero = document.querySelector(".am-collection-list--hero");
    if (!hero) return;

    const isDesktop = window.matchMedia("(min-width: 900px)").matches;
    const desktopOnlyItems = hero.querySelectorAll(".am-collection-list__item--hide-mobile img");
    const mobilePrimaryItems = hero.querySelectorAll(
      ".collection-list__item:not(.am-collection-list__item--hide-mobile) img",
    );

    if (isDesktop) {
      hero.querySelectorAll(".collection-list__item img").forEach((img) => {
        img.loading = "eager";
        img.setAttribute("fetchpriority", "high");
      });
    } else {
      desktopOnlyItems.forEach((img) => {
        img.loading = "lazy";
        img.setAttribute("fetchpriority", "low");
      });
      mobilePrimaryItems.forEach((img, index) => {
        img.loading = "eager";
        img.setAttribute("fetchpriority", index === 0 ? "high" : "auto");
      });
    }
  }

  function handleLazyContent(event) {
    const root = event.detail?.root || document;
    initPicLazy();
    prefetchLazyImages(root, { usePreload: false });
  }

  function init() {
    tuneHeaderLogoPriority();
    initProductGalleryPriority();
    tuneHomepageHeroImagePriority();
    initHeroImageReady();
    initPicLazy();
    initSectionPrefetch();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", () => {
    initHeroImageReady();
    initPicLazy();
    initSectionPrefetch();
  });

  document.addEventListener("facets:change", () => {
    initPicLazy();
  });

  document.addEventListener("am:lazy-content", handleLazyContent);
})();
